import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { supabase } from '../services/supabase'
import { useToast } from '../hooks/useToast'
import { ToastContainer } from './Toast'
import Button from './Button'

interface Modulo {
    id: string
    titulo: string
    descricao: string | null
    ordem_exibicao: number
}

interface ModalModuloProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    modulo?: Modulo | null
    cursoId: string
}

export default function ModalModulo({ isOpen, onClose, onSuccess, modulo, cursoId }: ModalModuloProps) {
    const { toasts, showSuccess } = useToast()
    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        ordem_exibicao: '1'
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (modulo) {
            setFormData({
                titulo: modulo.titulo,
                descricao: modulo.descricao || '',
                ordem_exibicao: modulo.ordem_exibicao.toString()
            })
        } else {
            resetForm()
        }
    }, [modulo, isOpen])

    const resetForm = () => {
        setFormData({
            titulo: '',
            descricao: '',
            ordem_exibicao: '1'
        })
        setError('')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            // Validações
            if (!formData.titulo.trim()) {
                setError('Título é obrigatório')
                return
            }

            if (!formData.ordem_exibicao || parseInt(formData.ordem_exibicao) <= 0) {
                setError('Ordem de exibição deve ser maior que zero')
                return
            }

            const moduloData = {
                titulo: formData.titulo.trim(),
                descricao: formData.descricao.trim() || null,
                ordem_exibicao: parseInt(formData.ordem_exibicao),
                curso_id: cursoId
            }

            if (modulo) {
                // Atualizar módulo existente
                const { error } = await supabase
                    .from('modulos')
                    .update(moduloData)
                    .eq('id', modulo.id)

                if (error) throw error
                showSuccess('Módulo atualizado com sucesso!')
            } else {
                // Criar novo módulo
                const { error } = await supabase
                    .from('modulos')
                    .insert(moduloData)

                if (error) throw error
                showSuccess('Módulo criado com sucesso!')
            }

            onSuccess()
            onClose()
            resetForm()
        } catch (err) {
            console.error('Erro ao salvar módulo:', err)
            setError('Erro ao salvar módulo. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {modulo ? 'Editar Módulo' : 'Novo Módulo'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Erro */}
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Título */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Título do Módulo *
                        </label>
                        <input
                            type="text"
                            value={formData.titulo}
                            onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Ex: Introdução às Vendas"
                            required
                        />
                    </div>

                    {/* Descrição */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Descrição
                        </label>
                        <textarea
                            value={formData.descricao}
                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                            rows={4}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Descreva o conteúdo deste módulo..."
                        />
                    </div>

                    {/* Ordem de Exibição */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Ordem de Exibição *
                        </label>
                        <input
                            type="number"
                            value={formData.ordem_exibicao}
                            onChange={(e) => setFormData({ ...formData, ordem_exibicao: e.target.value })}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="1"
                            min="1"
                            required
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Define a ordem em que este módulo aparecerá no curso
                        </p>
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            type="button"
                            variant="btn_secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="btn_add"
                            loading={loading}
                            disabled={loading}
                        >
                            {modulo ? 'Atualizar' : 'Criar'} Módulo
                        </Button>
                    </div>
                </form>
            </div>
            <ToastContainer toasts={toasts} />
        </div>
    )
}
